import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const BellCurve = () => {
  const [employees, setEmployees] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const empData = await api.get('/api/employees');
      setEmployees(empData);

      // Fetch actual ratings from Google Sheets (may fail if no ratings yet)
      try {
        const ratingsData = await api.get('/api/all-ratings');
        setRatings(ratingsData);
      } catch (ratingsErr) {
        console.warn('Could not fetch ratings:', ratingsErr);
        setRatings([]);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employee data');
      setEmployees([]);
      setRatings([]);
    }
    setLoading(false);
  };

  // Merge employees with their actual ratings from ratings sheet
  const getMergedData = () => {
    const ratingsMap = {};
    ratings.forEach((r) => {
      ratingsMap[r.employeeId] = r;
    });

    return employees.map((emp) => {
      const empId = emp.empID || emp.id;
      const ratingData = ratingsMap[empId];
      return {
        ...emp,
        actualRating: ratingData ? ratingData.overallRating : null,
        ratedBy: ratingData ? ratingData.ratedBy : null,
        ratingDate: ratingData ? ratingData.ratingDate : null,
        isRated: !!ratingData,
      };
    });
  };

  const getFilteredData = () => {
    const merged = getMergedData();
    if (selectedCategory === 'all') return merged;
    return merged.filter((emp) => emp.category === selectedCategory);
  };

  const filteredData = getFilteredData();
  const ratedEmployees = filteredData.filter((emp) => emp.isRated);
  const unratedEmployees = filteredData.filter((emp) => !emp.isRated);

  const getRatingDistribution = (data) => {
    const distribution = {
      '5.0': 0,
      '4.0': 0,
      '3.0': 0,
      '2.0': 0,
      '1.0': 0,
    };

    data.forEach((emp) => {
      const r = emp.actualRating;
      if (r == null) return;
      if (r >= 4.5) distribution['5.0']++;
      else if (r >= 3.5) distribution['4.0']++;
      else if (r >= 2.5) distribution['3.0']++;
      else if (r >= 1.5) distribution['2.0']++;
      else if (r > 0) distribution['1.0']++;
    });

    return distribution;
  };

  const getIdealDistribution = (total) => ({
    '5.0': Math.round(total * 0.1),
    '4.0': Math.round(total * 0.2),
    '3.0': Math.round(total * 0.4),
    '2.0': Math.round(total * 0.2),
    '1.0': Math.round(total * 0.1),
  });

  const actualDist = getRatingDistribution(filteredData);
  const idealDist = getIdealDistribution(ratedEmployees.length);

  const maxValue = Math.max(
    ...Object.values(actualDist),
    ...Object.values(idealDist),
    1
  );

  const avgRating =
    ratedEmployees.length > 0
      ? (ratedEmployees.reduce((sum, emp) => sum + emp.actualRating, 0) / ratedEmployees.length).toFixed(2)
      : '—';

  const ratingLevels = [
    { key: '5.0', label: 'Outstanding', color: '#8bc34a', range: '4.5–5.0' },
    { key: '4.0', label: 'Exceeds', color: '#0f3460', range: '3.5–4.4' },
    { key: '3.0', label: 'Meets', color: '#ffc107', range: '2.5–3.4' },
    { key: '2.0', label: 'Below', color: '#ff9800', range: '1.5–2.4' },
    { key: '1.0', label: 'Unsatisfactory', color: '#f44336', range: '0–1.4' },
  ];

  const styles = {
    container: { display: 'flex', flexDirection: 'column', gap: '24px' },
    filterContainer: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    button: {
      padding: '10px 20px', borderRadius: '6px', border: '1px solid #e0e0e0',
      backgroundColor: 'white', color: '#666', cursor: 'pointer',
      fontSize: '13px', fontWeight: '600', transition: 'all 0.3s ease',
    },
    buttonActive: { backgroundColor: '#e94560', color: 'white', borderColor: '#e94560' },
    card: {
      backgroundColor: 'white', borderRadius: '12px', padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    sectionTitle: { fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '20px' },
    chartContainer: {
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around',
      height: '300px', marginBottom: '24px', gap: '16px', padding: '20px',
      backgroundColor: '#f9f9f9', borderRadius: '8px',
    },
    barGroup: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flex: 1 },
    barWrapper: { display: 'flex', gap: '8px', alignItems: 'flex-end', height: '200px' },
    bar: {
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'flex-end', width: '30px', borderRadius: '4px 4px 0 0', minHeight: '20px',
    },
    barLabel: { fontSize: '12px', fontWeight: '600', color: '#333', marginTop: '8px', textAlign: 'center' },
    barValue: { fontSize: '11px', color: 'white', fontWeight: '700', marginBottom: '4px' },
    legend: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginTop: '20px' },
    legendItem: { display: 'flex', alignItems: 'center', gap: '12px' },
    legendColor: { width: '20px', height: '20px', borderRadius: '4px' },
    legendText: { fontSize: '13px', color: '#666' },
    stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginTop: '24px' },
    statBox: { backgroundColor: '#f9f9f9', padding: '16px', borderRadius: '8px', textAlign: 'center' },
    statLabel: { fontSize: '12px', color: '#999', marginBottom: '6px' },
    statValue: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: {
      textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e0e0e0',
      fontWeight: '700', color: '#1a1a2e', fontSize: '12px',
    },
    td: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', color: '#444' },
  };

  const getRatingBadge = (rating) => {
    let color = '#999';
    let label = '—';
    if (rating >= 4.5) { color = '#8bc34a'; label = 'Outstanding'; }
    else if (rating >= 3.5) { color = '#0f3460'; label = 'Exceeds'; }
    else if (rating >= 2.5) { color = '#ffc107'; label = 'Meets'; }
    else if (rating >= 1.5) { color = '#ff9800'; label = 'Below'; }
    else if (rating > 0) { color = '#f44336'; label = 'Unsatisfactory'; }
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '12px',
        backgroundColor: color + '20', color: color, fontWeight: '600', fontSize: '11px',
      }}>
        {rating.toFixed(2)} — {label}
      </span>
    );
  };

  return (
    <div style={styles.container}>
      {/* Category Filters */}
      <div style={styles.filterContainer}>
        {['all', 'Production', 'Quality Controllers', 'Project Managers'].map((cat) => (
          <button
            key={cat}
            style={{ ...styles.button, ...(selectedCategory === cat && styles.buttonActive) }}
            onClick={() => setSelectedCategory(cat)}
            onMouseEnter={(e) => { if (selectedCategory !== cat) e.target.style.backgroundColor = '#f0f0f0'; }}
            onMouseLeave={(e) => { if (selectedCategory !== cat) e.target.style.backgroundColor = 'white'; }}
          >
            {cat === 'all' ? 'All Categories' : cat}
          </button>
        ))}
      </div>

      {/* Summary Stats */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Appraisal Summary</div>
        {loading && <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>Loading...</div>}
        {error && <div style={{ padding: '24px', textAlign: 'center', color: '#f44336' }}>{error}</div>}
        {!loading && !error && (
          <div style={styles.stats}>
            <div style={styles.statBox}>
              <div style={styles.statLabel}>Total Employees</div>
              <div style={styles.statValue}>{filteredData.length}</div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statLabel}>Rated</div>
              <div style={{ ...styles.statValue, color: '#8bc34a' }}>{ratedEmployees.length}</div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statLabel}>Pending</div>
              <div style={{ ...styles.statValue, color: unratedEmployees.length > 0 ? '#ff9800' : '#8bc34a' }}>
                {unratedEmployees.length}
              </div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statLabel}>Avg Rating</div>
              <div style={styles.statValue}>{avgRating}</div>
            </div>
          </div>
        )}
      </div>

      {/* Bell Curve Chart */}
      {!loading && !error && ratedEmployees.length > 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Rating Distribution (Bell Curve)</div>

          <div style={styles.chartContainer}>
            {ratingLevels.map((level) => (
              <div key={level.key} style={styles.barGroup}>
                <div style={styles.barWrapper}>
                  <div
                    style={{
                      ...styles.bar, backgroundColor: level.color, opacity: 0.4,
                      height: `${(idealDist[level.key] / maxValue) * 200}px`,
                    }}
                  >
                    {idealDist[level.key] > 0 && <div style={styles.barValue}>{idealDist[level.key]}</div>}
                  </div>
                  <div
                    style={{
                      ...styles.bar, backgroundColor: level.color,
                      height: `${(actualDist[level.key] / maxValue) * 200}px`,
                    }}
                  >
                    {actualDist[level.key] > 0 && <div style={styles.barValue}>{actualDist[level.key]}</div>}
                  </div>
                </div>
                <div style={styles.barLabel}>{level.label}</div>
                <div style={{ fontSize: '11px', color: '#999' }}>{level.range}</div>
              </div>
            ))}
          </div>

          <div style={styles.legend}>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendColor, backgroundColor: '#ccc', opacity: 0.4 }}></div>
              <div style={styles.legendText}>Ideal Distribution (10-20-40-20-10)</div>
            </div>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendColor, backgroundColor: '#e94560' }}></div>
              <div style={styles.legendText}>Actual Distribution (from Ratings Sheet)</div>
            </div>
          </div>
        </div>
      )}

      {/* Bell Curve Visualization (Smooth Curve) */}
      {!loading && !error && ratedEmployees.length > 0 && (() => {
        const total = ratedEmployees.length;
        const categories = ['1.0', '2.0', '3.0', '4.0', '5.0'];
        const labels = ['Unsatisfactory', 'Below', 'Meets', 'Exceeds', 'Outstanding'];
        const colors = ['#f44336', '#ff9800', '#ffc107', '#0f3460', '#8bc34a'];

        // Actual percentages
        const actualPcts = categories.map(k => total > 0 ? (actualDist[k] / total) * 100 : 0);
        // Ideal bell curve percentages: 10-20-40-20-10
        const idealPcts = [10, 20, 40, 20, 10];

        const svgW = 700, svgH = 350;
        const padL = 60, padR = 30, padT = 40, padB = 70;
        const chartW = svgW - padL - padR;
        const chartH = svgH - padT - padB;
        const maxPct = Math.max(...actualPcts, ...idealPcts, 10);

        const xPos = categories.map((_, i) => padL + (i / (categories.length - 1)) * chartW);
        const yActual = actualPcts.map(p => padT + chartH - (p / maxPct) * chartH);
        const yIdeal = idealPcts.map(p => padT + chartH - (p / maxPct) * chartH);

        // Smooth curve path using cubic bezier
        const smoothPath = (points) => {
          if (points.length < 2) return '';
          let d = `M ${points[0][0]},${points[0][1]}`;
          for (let i = 0; i < points.length - 1; i++) {
            const x0 = points[i][0], y0 = points[i][1];
            const x1 = points[i + 1][0], y1 = points[i + 1][1];
            const cpx = (x1 - x0) * 0.4;
            d += ` C ${x0 + cpx},${y0} ${x1 - cpx},${y1} ${x1},${y1}`;
          }
          return d;
        };

        // Area fill path (curve + baseline)
        const areaPath = (points) => {
          const baseline = padT + chartH;
          let d = smoothPath(points);
          d += ` L ${points[points.length - 1][0]},${baseline} L ${points[0][0]},${baseline} Z`;
          return d;
        };

        const actualPoints = xPos.map((x, i) => [x, yActual[i]]);
        const idealPoints = xPos.map((x, i) => [x, yIdeal[i]]);

        // Y-axis grid lines
        const gridLines = [0, 25, 50, 75, 100].filter(v => v <= maxPct + 10);

        return (
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Bell Curve — Rating Distribution</div>
            <div style={{ display: 'flex', justifyContent: 'center', overflow: 'auto' }}>
              <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} style={{ fontFamily: 'inherit' }}>
                {/* Grid lines */}
                {gridLines.map(v => {
                  const y = padT + chartH - (v / maxPct) * chartH;
                  return (
                    <g key={v}>
                      <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#eee" strokeWidth="1" />
                      <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#999">{v}%</text>
                    </g>
                  );
                })}

                {/* Ideal curve — filled area */}
                <path d={areaPath(idealPoints)} fill="#0f3460" fillOpacity="0.08" />
                <path d={smoothPath(idealPoints)} fill="none" stroke="#0f3460" strokeWidth="2" strokeDasharray="8,4" opacity="0.5" />

                {/* Actual curve — filled area */}
                <path d={areaPath(actualPoints)} fill="#e94560" fillOpacity="0.15" />
                <path d={smoothPath(actualPoints)} fill="none" stroke="#e94560" strokeWidth="3" />

                {/* Data points on actual curve */}
                {actualPoints.map((pt, i) => (
                  <g key={i}>
                    <circle cx={pt[0]} cy={pt[1]} r="6" fill="#e94560" stroke="white" strokeWidth="2" />
                    <text x={pt[0]} y={pt[1] - 14} textAnchor="middle" fontSize="12" fontWeight="700" fill="#e94560">
                      {actualDist[categories[i]]} ({actualPcts[i].toFixed(0)}%)
                    </text>
                  </g>
                ))}

                {/* Data points on ideal curve */}
                {idealPoints.map((pt, i) => (
                  <g key={i}>
                    <circle cx={pt[0]} cy={pt[1]} r="4" fill="#0f3460" opacity="0.5" />
                  </g>
                ))}

                {/* X-axis labels */}
                {xPos.map((x, i) => (
                  <g key={i}>
                    <line x1={x} y1={padT + chartH} x2={x} y2={padT + chartH + 6} stroke="#ccc" />
                    <rect x={x - 12} y={padT + chartH + 10} width="24" height="18" rx="4"
                      fill={colors[i]} fillOpacity="0.15" />
                    <text x={x} y={padT + chartH + 24} textAnchor="middle" fontSize="11" fontWeight="700" fill={colors[i]}>
                      {categories[i].replace('.0', '')}
                    </text>
                    <text x={x} y={padT + chartH + 42} textAnchor="middle" fontSize="10" fill="#666">
                      {labels[i]}
                    </text>
                    <text x={x} y={padT + chartH + 56} textAnchor="middle" fontSize="9" fill="#999">
                      (Ideal: {idealPcts[i]}%)
                    </text>
                  </g>
                ))}

                {/* Axis lines */}
                <line x1={padL} y1={padT} x2={padL} y2={padT + chartH} stroke="#ddd" strokeWidth="1" />
                <line x1={padL} y1={padT + chartH} x2={svgW - padR} y2={padT + chartH} stroke="#ddd" strokeWidth="1" />
              </svg>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '30px', height: '3px', backgroundColor: '#e94560', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '12px', color: '#666' }}>Actual Distribution</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '30px', height: '0', borderTop: '2px dashed #0f3460', opacity: 0.5 }}></div>
                <span style={{ fontSize: '12px', color: '#666' }}>Ideal Bell Curve (10-20-40-20-10)</span>
              </div>
            </div>

            {/* Deviation summary */}
            <div style={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#1a1a2e', marginBottom: '8px' }}>Deviation from Ideal</div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {categories.map((k, i) => {
                  const diff = actualPcts[i] - idealPcts[i];
                  const diffColor = Math.abs(diff) < 5 ? '#8bc34a' : diff > 0 ? '#ff9800' : '#2196f3';
                  return (
                    <div key={k} style={{ fontSize: '12px', color: '#666' }}>
                      <span style={{ fontWeight: '600', color: colors[i] }}>{labels[i]}: </span>
                      <span style={{ fontWeight: '700', color: diffColor }}>
                        {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* No ratings message */}
      {!loading && !error && ratedEmployees.length === 0 && (
        <div style={styles.card}>
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Ratings Submitted Yet</div>
            <div style={{ fontSize: '13px' }}>
              Bell curve will populate once employees are rated and saved to the Ratings sheet.
            </div>
          </div>
        </div>
      )}

      {/* Employee-wise Ratings Table */}
      {!loading && !error && ratedEmployees.length > 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>
            Employee-wise Ratings ({ratedEmployees.length} rated)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Emp ID</th>
                  <th style={styles.th}>Employee Name</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Overall Rating</th>
                  <th style={styles.th}>Rated By</th>
                  <th style={styles.th}>Date</th>
                </tr>
              </thead>
              <tbody>
                {ratedEmployees
                  .sort((a, b) => (b.actualRating || 0) - (a.actualRating || 0))
                  .map((emp) => (
                    <tr key={emp.empID || emp.id}>
                      <td style={styles.td}>{emp.empID || emp.id}</td>
                      <td style={{ ...styles.td, fontWeight: '600' }}>{emp.name}</td>
                      <td style={styles.td}>{emp.category}</td>
                      <td style={styles.td}>{getRatingBadge(emp.actualRating)}</td>
                      <td style={styles.td}>{emp.ratedBy || '—'}</td>
                      <td style={styles.td}>{emp.ratingDate || '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unrated Employees */}
      {!loading && !error && unratedEmployees.length > 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>
            Pending Ratings ({unratedEmployees.length} employees)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Emp ID</th>
                  <th style={styles.th}>Employee Name</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Reporting Head</th>
                </tr>
              </thead>
              <tbody>
                {unratedEmployees.map((emp) => (
                  <tr key={emp.empID || emp.id}>
                    <td style={styles.td}>{emp.empID || emp.id}</td>
                    <td style={{ ...styles.td, fontWeight: '600' }}>{emp.name}</td>
                    <td style={styles.td}>{emp.category}</td>
                    <td style={styles.td}>{emp.reportingHead || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rating Scale Guide */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Rating Scale Guide</div>
        <div style={styles.legend}>
          {ratingLevels.map((level) => (
            <div key={level.key} style={{ ...styles.legendItem, paddingBottom: '12px' }}>
              <div style={{ ...styles.legendColor, backgroundColor: level.color }}></div>
              <div>
                <div style={{ ...styles.legendText, fontWeight: '600', color: '#1a1a2e' }}>{level.label}</div>
                <div style={{ fontSize: '12px', color: '#999' }}>{level.range}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BellCurve;
